import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { RouteComponentProps } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useDataProvider } from 'src/app/common/utils';
import { useLang } from 'src/app/i18n';
import {
  CampaignFormMode,
  CampaignSelectedSalesMaterialItem,
  SalesMaterialTypeEnum,
} from 'src/app/modules/PulseLeads/types/campaign-types';
import { fetchCampaignSaleMaterialList } from 'src/app/modules/PulseLeads/network/campaignCrud';
import { campaignPath } from '../CampaignRoutes';
import { fetchAllCategoryList } from 'src/app/modules/sales-kit/components/Library/network/libraryCrud';
import EditMaterialSetListTable, {
  CampaignMaterialSelectionPaginateList,
  CampaignMaterialSelectionSelectedRow,
} from './components/EditMaterialSetListTable';
import {
  clearCampaignMaterialSelectionDraft,
  getCampaignMaterialSelectionDraftKey,
  loadCampaignMaterialSelectionDraft,
  saveCampaignMaterialSelectionDraft,
} from './campaignMaterialSelectionDraft';

type MaterialSection = 'Sales' | 'Promotion' | 'HappyShare' | 'CustomerSurvey';

type CampaignMaterialSelectionFilterState = {
  page: number;
  pageSize: number;
  name: string;
  section: MaterialSection;
  category: string;
  status: string;
  type: string;
  createTimeStart: Date | null;
  createTimeEnd: Date | null;
  lastUpdatedTimeStart: Date | null;
  lastUpdatedTimeEnd: Date | null;
  effectiveDateStartFrom: Date | null;
  effectiveDateStartTo: Date | null;
  effectiveDateEndFrom: Date | null;
  effectiveDateEndTo: Date | null;
};

const DEFAULT_PAGE_SIZE = 10;

const LIBRARY_SECTIONS: Array<Extract<MaterialSection, 'Sales' | 'Promotion' | 'HappyShare'>> = [
  'Sales',
  'Promotion',
  'HappyShare',
];

const paramsInitiator = (): CampaignMaterialSelectionFilterState => ({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  name: '',
  section: 'Sales',
  category: '',
  status: '',
  type: '',
  createTimeStart: null,
  createTimeEnd: null,
  lastUpdatedTimeStart: null,
  lastUpdatedTimeEnd: null,
  effectiveDateStartFrom: null,
  effectiveDateStartTo: null,
  effectiveDateEndFrom: null,
  effectiveDateEndTo: null,
});

const useStyles = makeStyles()(() => ({
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 20,
    paddingBottom: 20,
  },
}));

const resourceTypeOptions = [
  { displayName: 'All', value: '' },
  { displayName: 'File', value: 'file' },
  { displayName: 'Link', value: 'link' },
  { displayName: 'Content', value: 'content' },
  { displayName: 'Material Set', value: 'materialSet' },
  { displayName: 'Text', value: 'text' },
  { displayName: 'Picture', value: 'picture' },
];

const sectionOptions = [
  { displayName: 'Sales Resources', value: 'Sales' },
  { displayName: 'Promotion Library', value: 'Promotion' },
  { displayName: 'Happy Share Library', value: 'HappyShare' },
  { displayName: 'Customer Survey', value: 'CustomerSurvey' },
];

const customerSurveyStatusValues = ['', 'Published', 'Unpublished', 'Draft'];

const isCustomerSurveySection = (section?: string) => section === 'CustomerSurvey';

const getMaterialSelectionKey = (item: { id?: string; uuid?: string }) => item.uuid || item.id || null;

const CampaignSalesMaterialSelectionPage: FC<RouteComponentProps> = ({ history, location }) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const locale = useLang();
  const dispatch = useDispatch();
  const Translation = (id: string) => intl.formatMessage({ id });

  const draftKey = useMemo(() => {
    const formMode = location.pathname.includes('/edit/') ? CampaignFormMode.EDIT : CampaignFormMode.CREATE;
    const campaignId = location.pathname.match(/\/edit\/([^/]+)/)?.[1];

    return getCampaignMaterialSelectionDraftKey(formMode, campaignId);
  }, [location.pathname]);

  const currentDraft = useMemo(() => loadCampaignMaterialSelectionDraft(draftKey), [draftKey]);
  const [filterState, setFilterState] = useState<CampaignMaterialSelectionFilterState>(paramsInitiator());
  const [filterFormState, setFilterFormState] = useState<CampaignMaterialSelectionFilterState>(paramsInitiator());
  const filterFormStateRef = useRef<CampaignMaterialSelectionFilterState>(filterFormState);
  const [tableVersion, setTableVersion] = useState(0);
  const [paginatedLibraryList, setPaginatedLibraryList] = useState<CampaignMaterialSelectionPaginateList>();
  const [categoryOptionList, setCategoryOptionList] = useState<{ displayName: string; value: string }[]>();
  const [selectedRowsState, setSelectedRows] = useState<CampaignSelectedSalesMaterialItem[]>(
    currentDraft?.selectedSalesMaterials || [],
  );

  const returnPath = currentDraft?.returnPath || `${campaignPath}/create`;

  useEffect(() => {
    if (!currentDraft) {
      clearCampaignMaterialSelectionDraft(draftKey);
      history.replace(`${campaignPath}/create`);
    }
  }, [currentDraft, draftKey, history]);

  const allOption = { displayName: Translation('component.status.all'), value: '' };
  const isSurveyFilter = isCustomerSurveySection(filterFormState.section);

  const filterOptions = {
    type: [allOption, ...resourceTypeOptions.slice(1)],
    status: [
      allOption,
      { displayName: Translation('component.status.published'), value: 'Published' },
      { displayName: Translation('component.status.unpublished'), value: 'Unpublished' },
      { displayName: Translation('component.status.draft'), value: 'Draft' },
      { displayName: Translation('component.status.publishedToSetOnly'), value: 'PublishedToSetOnly' },
    ],
  };

  const categoryChoices = isSurveyFilter ? [allOption] : categoryOptionList || [allOption];
  const typeChoices = isSurveyFilter ? [allOption] : filterOptions.type;
  const statusChoices = isSurveyFilter ? filterOptions.status.slice(0, 4) : filterOptions.status;

  const { isLoading, refreshData } = useDataProvider<CampaignMaterialSelectionPaginateList>(
    async () => {
      const result = await fetchCampaignSaleMaterialList(
        {
          page: filterState.page,
          limit: filterState.pageSize,
          name: filterState.name,
          section: filterState.section,
          category: filterState.category,
          status: filterState.status,
          type: filterState.type,
          createTimeStart: filterState.createTimeStart,
          createTimeEnd: filterState.createTimeEnd,
          lastUpdatedTimeStart: filterState.lastUpdatedTimeStart,
          lastUpdatedTimeEnd: filterState.lastUpdatedTimeEnd,
          effectiveDateStartFrom: filterState.effectiveDateStartFrom,
          effectiveDateStartTo: filterState.effectiveDateStartTo,
          effectiveDateEndFrom: filterState.effectiveDateEndFrom,
          effectiveDateEndTo: filterState.effectiveDateEndTo,
          locale,
        },
        dispatch,
      );

      return {
        libraryList: (result.items || []).map((item) => ({
          ...item,
        })),
        totalPages: result.totalPages,
        totalRecords: result.totalRecords,
      };
    },
    setPaginatedLibraryList,
    false,
    undefined,
    true,
  );

  // custom survey not have category, only get sales, promotion, happy share library
  useDataProvider<{ displayName: string; value: string }[]>(
    async () => {
      const categoryResults = await Promise.all(
        LIBRARY_SECTIONS.map((section) => fetchAllCategoryList(section, dispatch)),
      );
      const categorySet = new Set<string>();

      categoryResults.forEach((result) => {
        result.categoryList.forEach((item) => {
          if (item.name) {
            categorySet.add(item.name);
          }
        });
      });

      return [
        { displayName: Translation('component.status.all'), value: '' },
        ...Array.from(categorySet)
          .sort((left, right) => left.localeCompare(right))
          .map((name) => ({
            displayName: name,
            value: name,
          })),
      ];
    },
    setCategoryOptionList,
    false,
  );

  useEffect(() => {
    if (currentDraft) {
      refreshData();
    }
  }, [
    currentDraft,
    filterState.page,
    filterState.pageSize,
    filterState.name,
    filterState.section,
    filterState.category,
    filterState.status,
    filterState.type,
    filterState.createTimeStart,
    filterState.createTimeEnd,
    filterState.lastUpdatedTimeStart,
    filterState.lastUpdatedTimeEnd,
    filterState.effectiveDateStartFrom,
    filterState.effectiveDateStartTo,
    filterState.effectiveDateEndFrom,
    filterState.effectiveDateEndTo,
    locale,
  ]);

  const isSurveyMaterial = (item: CampaignMaterialSelectionSelectedRow) =>
    item.type === SalesMaterialTypeEnum.survey ||
    item.section === 'Customer Survey' ||
    ('sectionKey' in item && item.sectionKey === 'CustomerSurvey');

  const mapLibraryItemToSelectedMaterial = (
    item: CampaignMaterialSelectionSelectedRow,
  ): CampaignSelectedSalesMaterialItem => ({
    uuid: item.uuid,
    name: item.name,
    section: item.section,
    type: isSurveyMaterial(item) ? SalesMaterialTypeEnum.survey : SalesMaterialTypeEnum.material,
    status: item.status || '',
    order: 0,
  });

  const handleSelectedRowsChange = (rows: CampaignMaterialSelectionSelectedRow[]) => {
    const currentPageItemKeys = new Set(
      (paginatedLibraryList?.libraryList || [])
        .map(getMaterialSelectionKey)
        .filter((item): item is string => item !== null),
    );
    const currentPageSelections: CampaignSelectedSalesMaterialItem[] = rows
      .filter((item) => {
        const itemKey = getMaterialSelectionKey(item);

        return itemKey ? currentPageItemKeys.has(itemKey) : false;
      })
      .map(mapLibraryItemToSelectedMaterial);

    setSelectedRows((currentState) => [
      ...currentState.filter((item) => {
        const itemKey = getMaterialSelectionKey(item);

        return !itemKey || !currentPageItemKeys.has(itemKey);
      }),
      ...currentPageSelections,
    ]);
  };

  const normalizeFilterState = (
    data: any,
    currentState: CampaignMaterialSelectionFilterState,
  ): CampaignMaterialSelectionFilterState => {
    const section = (data.section || 'Sales') as MaterialSection;
    const status = data.status ?? '';
    const isCustomerSurveyFilter = isCustomerSurveySection(section);

    return {
      ...currentState,
      name: data.name,
      section,
      category: isCustomerSurveyFilter ? undefined : data.category,
      status: isCustomerSurveyFilter && !customerSurveyStatusValues.includes(status) ? undefined : status,
      type: isCustomerSurveyFilter ? undefined : data.type,
      createTimeStart: data.createTimeStart,
      createTimeEnd: data.createTimeEnd,
      lastUpdatedTimeStart: data.lastUpdatedTimeStart,
      lastUpdatedTimeEnd: data.lastUpdatedTimeEnd,
      effectiveDateStartFrom: data.effectiveDateStartFrom,
      effectiveDateStartTo: data.effectiveDateStartTo,
      effectiveDateEndFrom: data.effectiveDateEndFrom,
      effectiveDateEndTo: data.effectiveDateEndTo,
    };
  };

  const handleFilterFormChange = (data: any) => {
    const nextFilterFormState = normalizeFilterState(data, filterFormStateRef.current);

    filterFormStateRef.current = nextFilterFormState;
    setFilterFormState(nextFilterFormState);
  };

  const handleFilterSearch = () => {
    setFilterState({
      ...filterFormStateRef.current,
      page: 1,
      pageSize: filterState.pageSize,
    });
    setTableVersion((currentValue) => currentValue + 1);
  };

  const onBack = () => {
    history.push(returnPath);
  };

  const onSubmit = () => {
    const nextSelectedSalesMaterials = selectedRowsState.map((item, index) => ({
      ...item,
      order: index,
    }));

    saveCampaignMaterialSelectionDraft(draftKey, {
      ...currentDraft,
      selectedSalesMaterials: nextSelectedSalesMaterials,
      returnPath,
    });
    history.push(returnPath);
  };

  if (!currentDraft) {
    return null;
  }

  return (
    <div>
      <PruFilter
        key={`campaign-material-filter-${filterFormState.section}`}
        title={Translation('component.filterTitle.sales-material')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'name',
            initialValue: filterFormState.name,
            displayName: Translation('component.formLabel.name'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            style: { width: 220 },
            field: 'section',
            initialValue: filterFormState.section,
            defaultValue: 'Sales',
            displayName: Translation('pulseleads.campaign.salesMaterial.section'),
            choices: sectionOptions,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            style: { width: 250 },
            field: 'category',
            initialValue: filterFormState.category,
            displayName: Translation('component.formLabel.category'),
            choices: categoryChoices,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            style: { width: 180 },
            field: 'status',
            initialValue: filterFormState.status,
            displayName: Translation('component.formLabel.status'),
            choices: statusChoices,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            style: { width: 180 },
            field: 'type',
            initialValue: filterFormState.type,
            displayName: Translation('component.formLabel.type'),
            choices: typeChoices,
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'createTimeStart',
            fieldTo: 'createTimeEnd',
            initialDateFrom: filterFormState.createTimeStart,
            initialDateTo: filterFormState.createTimeEnd,
            displayName: Translation('component.formLabel.created-time'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'lastUpdatedTimeStart',
            fieldTo: 'lastUpdatedTimeEnd',
            initialDateFrom: filterFormState.lastUpdatedTimeStart,
            initialDateTo: filterFormState.lastUpdatedTimeEnd,
            displayName: Translation('component.formLabel.last-updated-time'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'effectiveDateStartFrom',
            fieldTo: 'effectiveDateStartTo',
            initialDateFrom: filterFormState.effectiveDateStartFrom,
            initialDateTo: filterFormState.effectiveDateStartTo,
            displayName: Translation('component.formLabel.effective-date-range-start'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'effectiveDateEndFrom',
            fieldTo: 'effectiveDateEndTo',
            initialDateFrom: filterFormState.effectiveDateEndFrom,
            initialDateTo: filterFormState.effectiveDateEndTo,
            displayName: Translation('component.formLabel.effective-date-range-end'),
          },
        ]}
        updateFilterOnChange
        onChangeFilter={handleFilterFormChange}
        fetchData={handleFilterSearch}
      />

      <EditMaterialSetListTable
        key={`campaign-material-table-${tableVersion}`}
        isLoading={isLoading}
        libraryList={paginatedLibraryList}
        onRefresh={refreshData}
        onSort={() => undefined}
        onChangePage={(page, rowsPerPage) => {
          setFilterState((currentState) => ({
            ...currentState,
            page,
            pageSize: rowsPerPage,
          }));
        }}
        enableCreate={true}
        defaultPageNumber={Math.max(filterState.page - 1, 0)}
        defaultRowsPerPage={filterState.pageSize}
        setCurrentSelectedRow={handleSelectedRowsChange}
        updateSelectedRow={selectedRowsState}
      />

      <div className={classes.footer}>
        <Button variant="contained" onClick={onBack}>
          {Translation('app.button.cancel')}
        </Button>
        <Button variant="contained" color="secondary" onClick={onSubmit}>
          {Translation('app.button.submit')}
        </Button>
      </div>
    </div>
  );
};

export default CampaignSalesMaterialSelectionPage;
